import React, { JSXElementConstructor } from 'react';
import { JSXIdentifier }                from 'react-jsx-parser/lib/types/types/acorn-jsx';

/* Basic type for a prop that can specify either a named HTML element or JSX / React class  */
export type AsTagProp = any; //React.ElementType | React.HTMLAttributes<HTMLOrSVGElement>

export interface ComponentWithAsProp {
	as?: AsTagProp
}

export interface ComponentWithLoadingFlag {
	loading?: boolean
}

/* Component that includes an 'as' prop which can take a named HTML element or JSX / React class */
export interface ComponentWithAsTagProp {
	/** An element type to render as (string or function). */
	as?: AsTagProp
}

/* UI */
export type HTMLHeaderSizes = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

/* Semantic supported named colors */
export enum SemanticUIColor {
	RED    = 'red',
	ORANGE = 'orange',
	YELLOW = 'yellow',
	OLIVE  = 'olive',
	GREEN  = 'green',
	TEAL   = 'teal',
	BLUE   = 'blue',
	VIOLET = 'violet',
	PURPLE = 'purple',
	PINK   = 'pink',
	BROWN  = 'brown',
	GREY   = 'grey',
	BLACK  = 'black'
}

/* Semantic supported named sizes, varies by component
but provided for consistent interop in Corval components */
export enum SemanticUISize {
	MINI    = 'mini',
	TINY    = 'tiny',
	SMALL   = 'small',
	MEDIUM  = 'medium',
	LARGE   = 'large',
	BIG     = 'big',
	HUGE    = 'huge',
	MASSIVE = 'massive',
}

/* Note to maintainers:
* These are derived from class colors
* generated from _colors.scss in _sass/global/vars
* folder. Please update when generating color names
* ------------------------------------------------ */

/* Extended Corval named colors */
export enum CorvalUIColor {
	WHITE            = 'white',
	GRAY             = 'gray',
	OBSIDIAN         = 'obsidian',
	ONYX             = 'onyx',
	LIGHTBLACK       = 'lightBlack',
	LIGHTBLUE        = 'lightBlue',
	LIGHTGREEN       = 'lightGreen',
	LIGHTORANGE      = 'lightOrange',
	LIGHTPINK        = 'lightPink',
	LIGHTOLIVE       = 'lightOlive',
	LIGHTPURPLE      = 'lightPurple',
	LIGHTRED         = 'lightRed',
	LIGHTTEAL        = 'lightTeal',
	LIGHTYELLOW      = 'lightYellow',
	BLUEBACKGROUND   = 'blueBackground',
	GREENBACKGROUND  = 'greenBackground',
	ORANGEBACKGROUND = 'orangeBackground',
	PINKBACKGROUND   = 'pinkBackground',
	OLIVEBACKGROUND  = 'oliveBackground',
	PURPLEBACKGROUND = 'purpleBackground',
	REDBACKGROUND    = 'redBackground',
	TEALBACKGROUND   = 'tealBackground',
	YELLOWBACKGROUND = 'yellowBackground',
	DARKBLUE         = 'darkBlue',
	DARKGREEN        = 'darkGreen',
	DARKORANGE       = 'darkOrange',
	DARKPINK         = 'darkPink',
	DARKOLIVE        = 'darkOlive',
	DARKPURPLE       = 'darkPurple',
	DARKRED          = 'darkRed',
	DARKTEAL         = 'darkTeal',
	DARKYELLOW       = 'darkYellow',
	OFFWHITE         = 'offWhite',
	DARKWHITE        = 'darkWhite',
	LIGHTGREY        = 'lightGrey',
	LT_GRAY          = 'lt-gray',
	XLT_GRAY         = 'xlt-gray',
	DARKGREY         = 'darkGrey',
	DK_GRAY          = 'dk-gray',
	XDK_GRAY         = 'xdk-gray',
	LT_SCREEN_85     = 'lt-screen-85',
	DK_SCREEN_85     = 'dk-screen-85',
	LT_SCREEN_50     = 'lt-screen-50',
	DK_SCREEN_50     = 'dk-screen-50',
	LT_SCREEN_25     = 'lt-screen-25',
	DK_SCREEN_25     = 'dk-screen-25',
	LT_SCREEN_10     = 'lt-screen-10',
	DK_SCREEN_10     = 'dk-screen-10',
	CLEAR            = 'clear',
	POSITIVE_VALUE   = 'positive-value',
	WARNING_VALUE    = 'warning-value',
	NEGATIVE_VALUE   = 'negative-value',
	SUCCESS          = 'success',
	DANGER           = 'danger',
	WARNING          = 'warning',
	PRIMARY          = 'primary',
	INFO             = 'info',
	SABLE            = 'sable',
	DOVER            = 'dover',
	ASHEN            = 'ashen',
	SLATE            = 'slate',
	LUNAR            = 'lunar',
	FEATHER          = 'feather',
	LIGHTMIDGRAY     = 'lightMidGray',
	DARKMIDGRAY      = 'darkMidGray',
	BRINE            = 'brine',
	LIGHTBRINE       = 'lightBrine',
	DARKBRINE        = 'darkBrine',
	HEADER           = 'header',
	TEXT             = 'text',
	TEXT_MUTED       = 'text-muted',
	BORDER           = 'border',
	HTML_BGCOLOR     = 'html-bgcolor ',
	BODY_BGCOLOR     = 'body-bgcolor ',
	LINK_COLOR       = 'link-color',
	LINK_HOVER_COLOR = 'link-hover-color',
}

export enum CorvalUtilHexColors {
	DARK_BLUE = '#111933',
	XLT_GRAY  = '#F7F7F7',
	WHITE     = '#FFFFFF',
}

/* Complete Corval palette named colors */
export type CorvalColor = SemanticUIColor | CorvalUIColor;

/* Note to maintainers:
* These are drived from theme colors
* generated from _vars.scss in _sass/components/container
* folder. Please update when generating theme names
* ------------------------------------------------ */

/* Corval named theme colors. Used in conjunction with
CSS to style text, border, bg of a container and its contents */
export enum CorvalColorTheme {
	WHITE   = 'white',
	FEATHER = 'feather',
	PRIMARY = 'primary',
	INFO    = 'info',
	SUCCESS = 'success',
	WARNING = 'warning',
	DANGER  = 'danger',
}

/* Corval CSS named sizes */
export enum Size {
	XS = 'xs',
	SM = 'sm',
	MD = 'md',
	LG = 'lg',
	XL = 'xl',
}

/* Corval CSS named flexbox justifications; use on anything acting as a flex container, or FlexBox, FlexItem component */
export enum FlexJustify {
	CENTER        = 'center',
	START         = 'start',
	END           = 'end',
	AROUND        = 'around',
	SPACE_BETWEEN = 'space-between',
}

/* Corval CSS named flexbox alignments; use on anything acting as a flex container, or FlexBox, FlexItem component */
export enum FlexAlign {
	START   = 'start',
	END     = 'end',
	STRETCH = 'stretch',
	CENTER  = 'center',
}

/* Corval CSS named flexbox flow control; use on anything acting as a flex container, or FlexBox, FlexItem component */
export enum FlexFlow {
	COLS         = 'cols',
	ROW_REVERSE  = 'row-reverse',
	COLS_REVERSE = 'cols-reverse',
}

// Alias for CSS Size for use on padding class props
export type PaddingSize = Size;

// Alias for CSS Size for use on margin class props
export type MarginSize = Size;

// Alias for CSS Size for use on flexbox container gutters
export type FlexGutterSize = Size;

// Alias for CSS Size for use on outline class props
export type OutlineClassSize = Size.SM | Size.MD | Size.LG;

// Alias for CSS Size for use on border class props
export type BorderClassSize = Size.SM | Size.MD | Size.LG;

export type BorderClassSides = 'top' | 'bottom' | 'left' | 'right';

export type Number0 = 0;
export type Number1 = 1;
export type Number50 = 50;
export type Number100 = 100;

/* Numeric range 2 - 99 */
export type NumberRange2to99 = 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
	| 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19
	| 20 | 21 | 22 | 23 | 24 | 25 | 26 | 27 | 28 | 29
	| 30 | 31 | 32 | 33 | 34 | 35 | 36 | 37 | 38 | 39
	| 40 | 41 | 42 | 43 | 44 | 45 | 46 | 47 | 48 | 49
	| 50 | 51 | 52 | 53 | 54 | 55 | 56 | 57 | 58 | 59
	| 60 | 61 | 62 | 63 | 64 | 65 | 66 | 67 | 68 | 69
	| 70 | 71 | 72 | 73 | 74 | 75 | 76 | 77 | 78 | 79
	| 80 | 81 | 82 | 83 | 84 | 85 | 86 | 87 | 88 | 89
	| 90 | 91 | 92 | 93 | 94 | 95 | 96 | 97 | 98 | 99
	| 100;

/* Numeric range 0 - 100 */
export type NumberRange0to100 = Number0 | Number1 | NumberRange2to99 | Number100;

/* Numeric range 0 - 99 */
export type NumberRange0to99 = Number0 | Number1 | NumberRange2to99;

/* Numeric range 1 - 100 */
export type NumberRange1to100 = Number1 | NumberRange2to99 | Number100;

/* Numeric range 1 - 99 */
export type NumberRange1to99 = Number1 | NumberRange2to99;

// Supported CSS z-index levels */
export type ZIndex = 0 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 1000 | 2000 | 5000;

// Named CSS positioning style for use on position class props
export enum Position {
	RELATIVE = 'relative',
	ABSOLUTE = 'absolute',
	FIXED    = 'fixed',
	STICKY   = 'sticky',
}

// Named CSS alignment style for use on vertical alignment class props
export enum AlignVertical {
	TOP    = 'top',
	BOTTOM = 'bottom',
}

// Named CSS alignment style for use on horizontal alignment class props
export enum Align {
	LEFT   = 'left',
	RIGHT  = 'right',
	CENTER = 'center',
}
